import { X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

export interface HouseholdMember {
  id: string;
  name: string;
  age: string;
  health_conditions: string;
  special_needs: string;
}

interface HouseholdMemberFormProps {
  member: HouseholdMember;
  index: number;
  onUpdate: (id: string, field: keyof HouseholdMember, value: string) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
  isCurrentUser?: boolean;
}

export const HouseholdMemberForm = ({
  member,
  index,
  onUpdate,
  onRemove,
  canRemove,
  isCurrentUser = false,
}: HouseholdMemberFormProps) => {
  const label = isCurrentUser ? "You" : `Person ${index + 1}`;
  
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-medium">{label}</h3>
        {canRemove && !isCurrentUser && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove(member.id)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor={`name-${member.id}`}>Name</Label>
          {isCurrentUser ? (
            <Input
              id={`name-${member.id}`}
              value={member.name}
              readOnly
              className="bg-muted cursor-not-allowed"
              placeholder="(synced from Full Name above)"
            />
          ) : (
            <Input
              id={`name-${member.id}`}
              value={member.name}
              onChange={(e) => onUpdate(member.id, "name", e.target.value)}
              placeholder="Enter name"
              required
            />
          )}
          {isCurrentUser && (
            <p className="text-xs text-muted-foreground mt-1">
              Auto-filled from your name above
            </p>
          )}
        </div>

        <div>
          <Label htmlFor={`age-${member.id}`}>Age</Label>
          <Input
            id={`age-${member.id}`}
            type="number"
            value={member.age}
            onChange={(e) => onUpdate(member.id, "age", e.target.value)}
            placeholder="Enter age"
            min="0"
            max="120"
            required
          />
        </div>

        <div>
          <Label htmlFor={`health-${member.id}`}>
            Health Conditions (optional)
          </Label>
          <Textarea
            id={`health-${member.id}`}
            value={member.health_conditions}
            onChange={(e) =>
              onUpdate(member.id, "health_conditions", e.target.value)
            }
            placeholder="Any relevant health conditions"
            rows={2}
          />
        </div>

        <div>
          <Label htmlFor={`special-${member.id}`}>
            Special Needs (optional)
          </Label>
          <Textarea
            id={`special-${member.id}`}
            value={member.special_needs}
            onChange={(e) =>
              onUpdate(member.id, "special_needs", e.target.value)
            }
            placeholder="Any special needs or considerations"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
};
